import { cn } from '@rocco/utils/cn';
import { LIFT } from '~/ui-rocco/button-system';

/**
 * The shared pill used for tab bars and the CTAs that sit beside them.
 *
 * Every variant is the SAME box — same height, padding, type and border width —
 * and differs only in colour. That is the whole point of the recipe: a filled
 * pill written without a border is 2.7px shorter and narrower than an outlined
 * one next to it, which is what made the profile tab bar look uneven. The
 * filled variants therefore carry a transparent border of the same width rather
 * than no border, and the height is pinned instead of being left to fall out of
 * padding plus whatever line-height the font resolves to.
 *
 * Sizes are rem so they hit these px at the 1920 design anchor and still scale
 * with browser zoom.
 */
// The corner is the ramp's, not a full round: at 46px this pill sits on the
// M step, whose corner is 16px. A stadium shape here is the one part of the
// old chrome that never joined the button system.
const NAV_PILL_BASE =
    'inline-flex h-[2.875rem] shrink-0 items-center justify-center gap-[0.375rem] whitespace-nowrap rounded-[1rem] border-[1.347px] px-[1.176rem] font-sans text-[1.008rem] leading-none transition-colors';

const NAV_PILL_VARIANTS = {
    /** Active tab / primary action on a light band. */
    'filled-light': 'border-transparent bg-[#1f1f21] text-white',
    /**
     * The page's own white CTA surface, in the band's geometry.
     *
     * For actions that are repeated from the body of the page: they read as
     * the same control the reader has already seen rather than as a new,
     * louder one appearing in the chrome.
     */
    'lifted-light': cn(
        'border-transparent bg-white text-[#323232] hover:bg-white/70',
        LIFT,
    ),
    /**
     * The same control over a dark band. White here would be the brightest
     * thing on a near-black section, so it steps down to a grey that still
     * reads as a raised surface; the lift does not come with it, since a soft
     * black shadow shows nothing against black.
     */
    'lifted-dark':
        'border-transparent bg-[#323232] text-white hover:bg-[#3d3d3d]',
    /** Inactive tab / secondary action on a light band. */
    'outlined-light':
        'border-[#181818] text-[#181818] hover:bg-[#181818] hover:text-white',
    /** Active tab / primary action on a dark band. */
    'filled-dark': 'border-transparent bg-white text-[#181818]',
    /** Inactive tab / secondary action on a dark band. */
    'outlined-dark':
        'border-white/40 text-white hover:bg-white hover:text-[#181818]',
    /**
     * Inside a `LensSwitch`: the travelling pane behind the label is the
     * selection, so the label carries no fill and no border of its own — one
     * here would sit on top of the pane and cover it. The border width stays,
     * transparent, so the box measures the same as the standalone pill.
     */
    // The pane behind the selected label is white now, so the label is the
    // page's own ink; the unselected ones step back a shade so the row still
    // says which is current when the pane's shadow is doing the rest.
    'switch-light': 'border-transparent bg-transparent text-[#181818]',
    'switch-light-rest':
        'border-transparent bg-transparent text-[#9a9a9e] hover:text-[#181818]',
    'switch-dark': 'border-transparent bg-transparent text-[#141414]',
    'switch-dark-rest':
        'border-transparent bg-transparent text-white/70 hover:text-white',
} as const;

export type NavPillVariant = keyof typeof NAV_PILL_VARIANTS;

/**
 * @param filled - the active/primary treatment; otherwise the hairline one.
 * @param dark - for a dark band.
 * @param lifted - the page's white CTA surface instead of fill-or-hairline.
 * @param inSwitch - the label sits inside a `LensSwitch`, which draws the
 *   selection behind it; the pill itself then carries neither fill nor border.
 */
export const navPill = ({
    filled = false,
    dark = false,
    lifted = false,
    inSwitch = false,
    className,
}: {
    filled?: boolean;
    dark?: boolean;
    lifted?: boolean;
    inSwitch?: boolean;
    className?: string;
} = {}) =>
    cn(
        NAV_PILL_BASE,
        // Inside the switch the labels are the page's section nav, not a row
        // of controls: 18px at medium, against the 16px the standalone pill
        // uses.
        inSwitch && 'text-[1.125rem] font-medium',
        inSwitch
            ? NAV_PILL_VARIANTS[
                  `switch-${dark ? 'dark' : 'light'}${filled ? '' : '-rest'}`
              ]
            : lifted
              ? NAV_PILL_VARIANTS[dark ? 'lifted-dark' : 'lifted-light']
              : NAV_PILL_VARIANTS[
                    `${filled ? 'filled' : 'outlined'}-${dark ? 'dark' : 'light'}`
                ],
        className,
    );
